// src/App.js
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Home from "./pages/Home";
import Investments from "./pages/Investments";
import InvestmentAdvertPage from "./pages/InvestmentAdvertPage";
import TransactionHistoryPage from "./pages/TransactionHistoryPage";
import LoansPage from "./pages/LoansPage";
import SettingsPage from "./pages/Settings";
import Statistics from './pages/Statistics';
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/Privacypolicy";
import Header from './components/Header';
import authService from './services/auth.service';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoggedIn, setIsLoggedIn] = useState(authService.isAuthenticated());
  const [authView, setAuthView] = useState('login');
  const [isLoading, setIsLoading] = useState(true);

  // =======================
  // Auto logout on token expiry
  // =======================

  const performLogout = useCallback((showMessage = true) => {
    authService.clearStorage();
    setIsLoggedIn(false);
    setAuthView('login');
    if (showMessage) {
      toast.info('Your session has expired. Please log in again.');
    }
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    // Register logout callback
    authService.onLogout(performLogout);

    // Listen for logout events from API interceptor
    const handleAuthLogout = () => {
      performLogout();
    };
    
    window.addEventListener('auth:logout', handleAuthLogout);

    // Check token validity periodically
    const checkTokenValidity = () => {
      const token = authService.getToken();
      if (token && authService.isTokenExpired(token)) {
        performLogout();
      }
    };

    // Check every minute
    const intervalId = setInterval(checkTokenValidity, 60000);

    // Also set a timer for exact expiry time
    const token = authService.getToken();
    if (token) {
      const timeUntilExpiry = authService.getTimeUntilExpiry(token);
      if (timeUntilExpiry > 0) {
        const timeoutId = setTimeout(() => {
          performLogout();
        }, timeUntilExpiry);

        return () => {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          window.removeEventListener('auth:logout', handleAuthLogout);
        };
      }
    }

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('auth:logout', handleAuthLogout);
    };
  }, [performLogout]);

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      setIsLoggedIn(authenticated);
      setIsLoading(false);
    };
    checkAuth();
  }, []);

  // =======================
  // Navigation helpers
  // =======================

  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/investments') return 'investments';
    if (path === '/marketplace') return 'marketplace';
    if (path === '/transactions') return 'transactions';
    if (path === '/loans') return 'loans';
    if (path === '/statistics') return 'statistics';
    if (path === '/settings') return 'settings';
    return 'home';
  };

  const currentPage = getCurrentPage();

  const handleNavigation = (page) => {
    if (page === 'home') navigate('/');
    if (page === 'investments') navigate('/investments');
    if (page === 'marketplace') navigate('/marketplace');
    if (page === 'transactions') navigate('/transactions');
    if (page === 'loans') navigate('/loans');
    if (page === 'statistics') navigate('/statistics');
    if (page === 'settings') navigate('/settings');
  };

  // =======================
  // Auth handlers
  // =======================

  const handleLogin = (userId) => {
    setIsLoggedIn(true);
    setAuthView('login');
    navigate('/');
  };

  const handleSignUp = (userId) => {
    setIsLoggedIn(true);
    setAuthView('login');
    navigate('/');
  };

  const handleLogout = () => {
    authService.clearStorage();
    setIsLoggedIn(false);
    setAuthView('login');
    navigate('/');
  };

  const switchToSignUp = () => {
    setAuthView('signup');
  };

  const switchToLogin = () => {
    setAuthView('login');
  };

  const switchToForgotPassword = () => {
    setAuthView('forgot-password');
  };

  // =======================
  // Loading state
  // =======================

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // =======================
  // Public routes — no auth required
  // =======================

  if (location.pathname.startsWith('/reset-password')) {
    return (
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    );
  }

  if (location.pathname === '/privacy-policy') {
    return (
      <Routes>
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      </Routes>
    );
  }

  // =======================
  // Guard: Login/SignUp/ForgotPassword first
  // =======================

  if (!isLoggedIn) {
    if (authView === 'signup') {
      return <SignUp onSignUp={handleSignUp} onSwitchToLogin={switchToLogin} />;
    }
    if (authView === 'forgot-password') {
      return <ForgotPassword onBackToLogin={switchToLogin} />;
    }
    return (
      <Login 
        onLogin={handleLogin} 
        onSwitchToSignUp={switchToSignUp}
        onForgotPassword={switchToForgotPassword}
      />
    );
  }

  // =======================
  // App Layout
  // =======================

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <Header
        currentPage={currentPage}
        onNavigate={handleNavigation}
        onLogout={handleLogout}
      />

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/investments" element={<Investments />} />
        <Route path="/marketplace" element={<InvestmentAdvertPage />} />
        <Route path="/transactions" element={<TransactionHistoryPage />} />
        <Route path="/loans" element={<LoansPage />} />
        <Route path="/statistics" element={<Statistics />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
      
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        style={{ zIndex: 9999 }}
      />
    </BrowserRouter>
  );
}

export default App;